// Utility functions to save and load user settings from local storage
import { v4 as uuidv4 } from 'uuid';
import { Debug } from '../../shared/debug';

import { PlayerAccount, TrainingLevel } from "../../shared/playerAccount";
// Type-only: gameClient imports this module back, so import here would be circular
import type { GameClient } from './gameClient';
import { getQueryParams } from '../../shared/utils';
import { ChecklistAchievements, Multiplayer, SteamAchievements } from '../../shared/constants';
import { GameTotalsByMode } from '../../shared/replayStats';

export interface UserSettings {
    ladderRank?: number;
    gameTotals?: GameTotalsByMode;
    gamesToday?: { day: string, count: number };
    mainVolume?: number;
    musicVolume?: number;
    sfxVolume?: number;
    frequentVolume?: number;
    dontShowTutorial?: boolean;
    dismissedTutorials?: Array<string>;
    inGameTutorial: boolean;
    account: PlayerAccount;
    unannouncedAchievements: Array<string>;
    userId?: string; // UUID
    dismissedEAPopup?: boolean;
    uploadedReplays?: Array<string>;
}

let userSettingsCache: UserSettings | null = null;

export function saveUserSettings(settings: UserSettings) {
    localStorage.setItem('userSettings', JSON.stringify(settings));
    userSettingsCache = settings; // Update the cache
}

export function getUserSettings(): UserSettings {
    if (!userSettingsCache) {
        userSettingsCache = loadUserSettings();
    }
    return userSettingsCache;
}

function defaultUserSettings(): UserSettings {
    return {
        ladderRank: 0,
        mainVolume: .5,
        musicVolume: .5,
        sfxVolume: .5,
        frequentVolume: .5,
        dontShowTutorial: false,
        inGameTutorial: true,
        dismissedTutorials: [],
        account: { trainingLevel: TrainingLevel.NewPlayer, achievements: [] },
        unannouncedAchievements: [],
        userId: uuidv4(),
        dismissedEAPopup: false,
    };
}

function loadUserSettings(): UserSettings {
    const defaults = defaultUserSettings();
    const stored = localStorage.getItem('userSettings');
    if (!stored) {
        userSettingsCache = defaults;
        return userSettingsCache;
    }

    try {
        // Settings written by an older build are missing every field added since,
        // so merge them over the defaults
        const saved = JSON.parse(stored) as Partial<UserSettings>;
        userSettingsCache = {
            ...defaults,
            ...saved,
            account: { ...defaults.account, ...saved.account },
        };
    }
    catch (e) {
        // Corrupt settings shouldn't lock the player out of the game.
        console.error('Could not read stored user settings, falling back to defaults', e);
        userSettingsCache = defaults;
    }
    return userSettingsCache;
}

export function getLadderRank() {
    const settings = getUserSettings();
    return settings.ladderRank || 0;
}

export function setLadderRank(rank: number) {
    const settings = getUserSettings();
    if (rank < 0) rank = 0;
    settings.ladderRank = rank;
    saveUserSettings(settings);
}

export function getMainVolume(): number {
    return getUserSettings().mainVolume || .5;
}

export function setMainVolume(volume: number) {
    const settings = getUserSettings();
    settings.mainVolume = volume;
    saveUserSettings(settings);
}

export function getMusicVolume(): number {
    return getUserSettings().musicVolume || .5;
}

export function setMusicVolume(volume: number) {
    const settings = getUserSettings();
    settings.musicVolume = volume;
    saveUserSettings(settings);
}

export function getSfxVolume(): number {
    return getUserSettings().sfxVolume || .5;
}

export function setSfxVolume(volume: number) {
    const settings = getUserSettings();
    settings.sfxVolume = volume;
    saveUserSettings(settings);
}

export function getFrequentVolume(): number {
    const frequentVolume = getUserSettings().frequentVolume;
    return frequentVolume !== undefined ? frequentVolume : .5;
}

export function setFrequentVolume(volume: number) {
    const settings = getUserSettings();
    settings.frequentVolume = volume;
    saveUserSettings(settings);
}

export function getDontShowTutorial(): boolean {
    return getUserSettings().dontShowTutorial || false;
}

export function setDontShowTutorial(dontShow: boolean) {
    const settings = getUserSettings();
    settings.dontShowTutorial = dontShow;
    saveUserSettings(settings);
}

export function getIngameTutorial(): boolean {
    //TODO: This gets called often, and always loads json
    let ingameTutorial = getUserSettings().inGameTutorial;
    ingameTutorial = ingameTutorial !== undefined ? ingameTutorial : true;
    const noTut = getDontShowTutorial();
    return ingameTutorial && !noTut && !Debug.SkipTutorial;
}

export function setIngameTutorial(show: boolean) {
    const settings = getUserSettings();
    settings.inGameTutorial = show;
    saveUserSettings(settings);
}

export function getDismissedEAPopup(): boolean {
    if (Debug.NoEAPopup) return true;
    const queryParams = getQueryParams(window.location.href);
    const lobbyToJoin = queryParams['lobby'];
    const isSpawnGame = (lobbyToJoin !== undefined);
    if (isSpawnGame) {
        return true;
    }
    const dismissed = getUserSettings().dismissedEAPopup;
    return dismissed !== undefined ? dismissed : false;
}

export function setDismissedEAPopup(show: boolean) {
    const settings = getUserSettings();
    settings.dismissedEAPopup = show;
    saveUserSettings(settings);
}

export function getSeenTutorial(tutorialName: string): boolean {
    const settings = getUserSettings();
    return settings.dismissedTutorials?.includes(tutorialName) || false;
}

export function setSeenTutorial(tutorialName: string) {
    const settings = getUserSettings();
    const dismissedTutorials = settings.dismissedTutorials || [];
    dismissedTutorials.push(tutorialName);
    settings.dismissedTutorials = dismissedTutorials;
    saveUserSettings(settings);
}

export function getUserId(): string {
    const userId = getUserSettings().userId;
    if (userId === undefined) {
        const newId = uuidv4();
        setUserId(newId);
        return newId;
    }
    return userId;
}

export function setUserId(userId: string) {
    const settings = getUserSettings();
    settings.userId = userId;
    saveUserSettings(settings);
}

export function getTrainingLevel(): TrainingLevel {
    if (Debug.SkipTutorial) return TrainingLevel.Trained;
    return getUserSettings().account.trainingLevel || TrainingLevel.NewPlayer;
}

export function advanceTrainingLevel(client: GameClient) {
    if (client.isSpawnGame) return false;
    const trainingLevel = getTrainingLevel();
    if (trainingLevel === TrainingLevel.Trained) return false;
    let nextLevel: TrainingLevel;
    switch (trainingLevel) {
        case TrainingLevel.NewPlayer:
            nextLevel = TrainingLevel.SecondGame;
            break;
        case TrainingLevel.SecondGame:
            nextLevel = TrainingLevel.MultiplayerIntro;
            break;
        case TrainingLevel.MultiplayerIntro:
            nextLevel = TrainingLevel.Trained;
            break;
    }
    console.log("Advancing to training level", nextLevel);
    setTrainingLevel(nextLevel);
    return true;
}

export function setTrainingLevel(level: TrainingLevel) {
    const settings = getUserSettings();
    settings.account.trainingLevel = level;
    saveUserSettings(settings);
}

export function resetTutorials() {
    const settings = getUserSettings();
    settings.dismissedTutorials = [];
    saveUserSettings(settings);
}

export function clearUnannouncedAchievements() {
    const settings = getUserSettings();
    if (!settings.unannouncedAchievements?.length) return;
    settings.unannouncedAchievements = [];
    saveUserSettings(settings);
}

export function getUploadedReplays(): Array<string> {
    return getUserSettings().uploadedReplays ?? [];
}

export function rememberUploadedReplay(fileName: string) {
    const settings = getUserSettings();
    settings.uploadedReplays = [...(settings.uploadedReplays ?? []), fileName].slice(-Multiplayer.MaxRememberedUploadedReplays);
    saveUserSettings(settings);
}

export function setUserAchievement(achievement: SteamAchievements) {
    const settings = getUserSettings();
    const achievementStr = achievement.toString();
    if (settings.account.achievements === undefined) {
        settings.account.achievements = [];
    }
    if (settings.unannouncedAchievements === undefined) {
        settings.unannouncedAchievements = [];
    }
    if (!settings.account.achievements.includes(achievementStr)) {
        settings.account.achievements.push(achievementStr);
        if (ChecklistAchievements.includes(achievementStr)) {
            settings.unannouncedAchievements.push(achievementStr);
        }
        saveUserSettings(settings);
    }
}